/**
 * Ordering helpers for product recommendation carousels.
 *
 * The product search used by the carousels returns results in index order, not in the order the
 * part numbers were requested, so the priority configured in CMC (displaySequence) or returned by
 * Emarsys is lost unless the products are re-sorted on our side. The React store did this in
 * use-espot.tsx (processProductRecommendation / processMarketingSpotData).
 */

import { ESpotContainerType } from '@/data/Content/_ESpotDataFromName';
import { DISPLAY_SEQ, MARKETING_SPOT_DATA_TYPE } from '@/data/constants/marketing';
import { dFix } from '@/data/utils/floatingPoint';

const CATALOG_ENTRY_TYPES = [
	MARKETING_SPOT_DATA_TYPE.CATALOG_ENTRY,
	MARKETING_SPOT_DATA_TYPE.CATALOG_ENTRY_ID,
];

/**
 * Part numbers of the eSpot's catalog entries, sorted by the displaySequence configured in CMC.
 * Only the first activity is used, the same as the React store, so SKUs of different activities
 * are not mixed. Entries without a displaySequence go last and keep their relative order.
 */
export const getPrioritizedPartNumbers = (spot?: ESpotContainerType): string[] => {
	const activities = spot?.MarketingSpotData?.at(0)?.baseMarketingSpotActivityData ?? [];
	const referenceId = activities.at(0)?.activityIdentifierID;

	return activities
		.filter(
			({ activityIdentifierID, baseMarketingSpotDataType, productPartNumber }) =>
				activityIdentifierID === referenceId &&
				CATALOG_ENTRY_TYPES.includes(baseMarketingSpotDataType as string) &&
				productPartNumber
		)
		.map(({ productPartNumber, properties }) => {
			const sequence = properties?.find(
				({ baseMarketingKey }) => baseMarketingKey === DISPLAY_SEQ
			)?.baseMarketingValue;
			return {
				partNumber: productPartNumber as string,
				sequence: sequence !== undefined && sequence !== '' ? dFix(sequence) : NaN,
			};
		})
		.sort((a, b) =>
			isNaN(a.sequence)
				? isNaN(b.sequence)
					? 0
					: 1
				: isNaN(b.sequence)
				? -1
				: a.sequence - b.sequence
		)
		.map(({ partNumber }) => partNumber);
};

/**
 * Sorts products to follow the order of the requested part numbers. Products whose part number
 * was not requested go last, keeping their relative order.
 */
export const sortByPartNumberOrder = <T extends { partNumber: string }>(
	products: T[],
	partNumbers: string[]
): T[] => {
	const position = new Map(partNumbers.map((partNumber, index) => [partNumber, index]));
	return [...products].sort(
		(a, b) =>
			(position.get(a.partNumber) ?? Number.MAX_SAFE_INTEGER) -
			(position.get(b.partNumber) ?? Number.MAX_SAFE_INTEGER)
	);
};
